import {globalConst, signalGradeEnum} from 'utils/const/globalConst'

/**
 * 설치 판정(전파 세기 x 전파 품질) 기준 설정.
 *
 * 기준이 지역·시기에 따라 조정되므로 코드에 하드코딩하지 않고 설정으로 분리한다.
 * 배포 후에도 서버의 JSON 파일만 교체하면 기준이 바뀌며, 파일을 읽지 못하거나
 * 형식이 어긋나면 기본값(기존 하드코딩 값)으로 계속 동작한다.
 */
export interface SignalConfig {
  /** RSRP 구간 경계. 내림차순이며 "이 값 이상"이 해당 열이 된다. */
  rsrpThresholds: number[]
  /** RSRQ 구간 경계. 내림차순이며 "이 값 이상"이 해당 행이 된다. */
  rsrqThresholds: number[]
  /** [RSRQ 행][RSRP 열] 판정표. 행/열 수는 경계 개수 + 1 이다. */
  matrix: signalGradeEnum[][]
}

/** 설정 파일 경로. docker-compose 볼륨 마운트 대상이라 임의로 바꾸지 않는다. */
export const SIGNAL_CONFIG_PATH = '/config/signal-config.json'

const {GOOD, NORMAL, NEED_CHECK} = signalGradeEnum

/**
 * 기본 기준값.
 *
 * |            | ≥-90     | -90~-100 | -100~-110 | <-110    |
 * |------------|----------|----------|-----------|----------|
 * | ≥-10       | 양호     | 양호     | 양호      | 보통     |
 * | -10 ~ -13  | 양호     | 양호     | 보통      | 확인필요 |
 * | -13 ~ -16  | 보통     | 보통     | 보통      | 확인필요 |
 * | <-16       | 확인필요 | 확인필요 | 확인필요  | 확인필요 |
 */
export const DEFAULT_SIGNAL_CONFIG: SignalConfig = {
  rsrpThresholds: [globalConst.rssiWarningdBm, globalConst.rsrpWarning, globalConst.rsrpBad],
  rsrqThresholds: [globalConst.rsrqGood, globalConst.rsrqInstallWarning, globalConst.rsrqInstallBad],
  matrix: [
    [GOOD, GOOD, GOOD, NORMAL],
    [GOOD, GOOD, NORMAL, NEED_CHECK],
    [NORMAL, NORMAL, NORMAL, NEED_CHECK],
    [NEED_CHECK, NEED_CHECK, NEED_CHECK, NEED_CHECK],
  ],
}

/**
 * JSON 에는 라벨(양호/보통/확인필요) 대신 영문 키를 쓴다.
 * 화면 문구가 바뀌어도 저장된 설정이 깨지지 않도록 하기 위함이다.
 */
type SignalGradeKey = keyof typeof signalGradeEnum

const gradeFromKey = (key: unknown): signalGradeEnum | null => {
  if (typeof key !== 'string') return null
  return signalGradeEnum[key as SignalGradeKey] ?? null
}

const keyFromGrade = (grade: signalGradeEnum): SignalGradeKey =>
  (Object.keys(signalGradeEnum) as SignalGradeKey[]).find(
    (key) => signalGradeEnum[key] === grade
  ) as SignalGradeKey

/** 현재 적용 중인 설정. 판정은 렌더 중 동기로 호출되므로 모듈 레벨에 캐시한다. */
let currentConfig: SignalConfig = DEFAULT_SIGNAL_CONFIG

export interface SignalConfigStatus {
  /** default = 기본값으로 동작 중, remote = 설정 파일 적용됨 */
  source: 'default' | 'remote'
  /** 마지막으로 설정 파일을 적용한 시각 */
  loadedAt: Date | null
  /** 마지막 실패 사유. 서버 접속 없이 원인을 확인해야 하므로 남겨둔다. */
  error: string | null
}

let currentStatus: SignalConfigStatus = {source: 'default', loadedAt: null, error: null}

export const getSignalConfig = (): SignalConfig => currentConfig
export const getSignalConfigStatus = (): SignalConfigStatus => currentStatus

/**
 * 측정값이 속한 구간 인덱스.
 * 어떤 경계에도 걸리지 않으면(가장 낮은 구간) 마지막 인덱스가 된다.
 */
export const gradeIndexOf = (value: number, thresholds: number[]): number => {
  const idx = thresholds.findIndex((threshold) => value >= threshold)
  return idx === -1 ? thresholds.length : idx
}

const isDescendingNumbers = (value: unknown): value is number[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every((v) => typeof v === 'number' && Number.isFinite(v)) &&
  value.every((v, i) => i === 0 || v < value[i - 1])

/**
 * 외부 JSON 을 검증해 SignalConfig 로 변환한다.
 * 한 군데라도 어긋나면 통째로 거부한다 — 일부만 반영되면 판정 기준이 뒤섞여 더 위험하다.
 *
 * @returns 유효하면 설정, 아니면 사유 문자열
 */
export const parseSignalConfig = (raw: any): SignalConfig | string => {
  if (!raw || typeof raw !== 'object') return '설정 형식이 올바르지 않습니다.'

  const {rsrpThresholds, rsrqThresholds, matrix} = raw
  if (!isDescendingNumbers(rsrpThresholds)) return 'rsrpThresholds 는 내림차순 숫자 배열이어야 합니다.'
  if (!isDescendingNumbers(rsrqThresholds)) return 'rsrqThresholds 는 내림차순 숫자 배열이어야 합니다.'

  const rowCount = rsrqThresholds.length + 1
  const colCount = rsrpThresholds.length + 1
  if (!Array.isArray(matrix) || matrix.length !== rowCount) {
    return `matrix 는 ${rowCount}개 행이어야 합니다.`
  }

  const parsedMatrix: signalGradeEnum[][] = []
  for (let row = 0; row < rowCount; row++) {
    const cells = matrix[row]
    if (!Array.isArray(cells) || cells.length !== colCount) {
      return `matrix ${row + 1}행은 ${colCount}개 열이어야 합니다.`
    }
    const parsedRow: signalGradeEnum[] = []
    for (let col = 0; col < colCount; col++) {
      const grade = gradeFromKey(cells[col])
      if (grade === null) {
        return `matrix ${row + 1}행 ${col + 1}열 값(${cells[col]})을 알 수 없습니다.`
      }
      parsedRow.push(grade)
    }
    parsedMatrix.push(parsedRow)
  }

  return {rsrpThresholds, rsrqThresholds, matrix: parsedMatrix}
}

/** 편집 UI 에서 저장할 때 쓰는 직렬화. 등급은 영문 키로 나간다. */
export const stringifySignalConfig = (config: SignalConfig): string =>
  JSON.stringify(
    {
      rsrpThresholds: config.rsrpThresholds,
      rsrqThresholds: config.rsrqThresholds,
      matrix: config.matrix.map((row) => row.map(keyFromGrade)),
    },
    null,
    2
  )

/**
 * 설정 파일을 읽어 적용한다.
 *
 * 현장에서 통신이 불안정해도 작업이 막히면 안 되므로, 어떤 실패에서도 예외를 던지지 않고
 * 직전 설정(없으면 기본값)을 그대로 유지한다.
 */
export const loadSignalConfig = async (timeoutMs: number = 3000): Promise<SignalConfig> => {
  /* 현장 통신이 끊긴 상태에서 응답을 기다리며 멈추지 않도록 제한을 둔다 */
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)

  try {
    const res = await fetch(`${SIGNAL_CONFIG_PATH}?ts=${Date.now()}`, {
      cache: 'no-store',
      signal: controller.signal,
    })
    const contentType = res.headers.get('content-type') ?? ''

    /* 파일이 없으면 nginx SPA fallback 이 index.html 을 200 으로 돌려주므로
       상태 코드만으로는 판별할 수 없다. */
    if (!res.ok || !contentType.includes('json')) {
      currentStatus = {...currentStatus, error: '설정 파일을 찾을 수 없습니다.'}
      return currentConfig
    }

    const parsed = parseSignalConfig(await res.json())
    if (typeof parsed === 'string') {
      currentStatus = {...currentStatus, error: parsed}
      return currentConfig
    }

    currentConfig = parsed
    currentStatus = {source: 'remote', loadedAt: new Date(), error: null}
  } catch {
    currentStatus = {...currentStatus, error: '설정 파일을 불러오지 못했습니다.'}
  } finally {
    clearTimeout(timer)
  }
  return currentConfig
}
